import { definePreset } from "@primeng/themes";
import Aura from "@primeng/themes/aura";
import { table_theme_configuration } from "./component-configuration/p-table.theme.configuration";
import { pcard_theme_configuration } from "./component-configuration/p-card.theme.configuration";
import { header_tags_extension } from "./extensions/header-tags.extension";
import { nadelThemeExtension } from "./theme.nadel.extension";
import { table_extension } from "./extensions/table.extension";
import { text_extension } from "./extensions/text.extension";
import { header_extension } from "./extensions/header-extension";
import { sidebar_extension } from "./extensions/sidebar.extension";
import { paginator_configuration } from "./component-configuration/paginator.configuration";
import { toggleswitch_configuration } from "./component-configuration/toggleswitch.configuration";
import { DrawerTokenSections } from "./component-configuration/p-drawer.theme.configuration";
import { pinputfield_theme_configuration } from "./component-configuration/p-input-fields.theme.configuration";
import { menu_theme_configuration } from "./component-configuration/p-menu.theme.configuration";

//* Include global properties of ng-prime components into component token as defined below, use seperate file for each component *//

const Nadel = definePreset(Aura, {
  components: {
    datatable: table_theme_configuration,
    card: pcard_theme_configuration,
    paginator: paginator_configuration,
    togglesswitch: toggleswitch_configuration,
    drawer: DrawerTokenSections,
    inputfield: pinputfield_theme_configuration,
    menu: menu_theme_configuration,
  },
  primitive: {
    nadelblue: {
      50: "#E6F4FF",
      100: "#BAE0FF",
      200: "#91CAFF",
      300: "#69B1FF",
      400: "#4096FF",
      500: "#1C3086",
      600: "#0958D9",
      700: "#003EB3",
      800: "#002C8C",
      900: "#001D66",
      950: "#001D66",
    },
    activelink: {
      400: "#1C3086",
      500: "#65A9F3",
    },
    scrollbarhover: {
      400: "#D9DFE3",
      500: "#33414C",
    },
    dashboardheader: {
      400: "#E6F4FF",
      500: "#111A2C",
    },
    dashboardheadertext: {
      400: "#1C3086",
      500: "#65A9F3",
    },
    tableheaderborder: {
      400: "#EBEEF3",
      500: "#313A49",
    },
    sortrow: {
      400: "#FCFCFD",
      500: "#010622",
    },
    tableinput: {
      400: "#D9D9D9",
      500: "#424242",
    },
    tableinputbg: {
      400: "#fff",
      500: "#010622",
    },
    bgdarkblue: {
      400: "#fff",
      500: "#010622",
    },
    bggraybg: {
      400: "#F6F7F9",
      500: "#151827",
    },
    alertlightbg: {
      400: "#f8f9fa",
      500: "#151827",
    },
    h2color: {
      400: "#000000E0",
      500: "#FFFFFFD9",
    },
    h3color: {
      400: "#000000a6",
      500: "#FFFFFFA6",
    },
    textblack: {
      400: "#000000E0",
      500: "#EDEDED",
    },
    textgray: {
      400: "#485464",
      500: "#EDEDED",
    },
    sidebartext: {
      400: "#fff",
      500: "#fff",
    },
    redcoltext: {
      400: "#AD393A",
      500: "#DC4446",
    },
    striplisting: {
      400: "#F0F5FF",
      500: "#151827",
    },
    errorcol: {
      400: "#DC4446",
      500: "#DC4446",
    },
    darkred: {
      400: "#830606",
      500: "#830606",
    },
    focuscol: {
      400: "#3C89E8",
      500: "#3C89E8",
    },
    clientbdgecol: {
      400: "#e6f4ff",
      500: "#1C3086",
    },
    clientbdgecoltext: {
      400: "#1C3086",
      500: "#fff",
    },
    prosuccess: {
      400: "#F6FFED",
      500: "#3C8618",
    },
    prowarning: {
      400: "#EBEEF3",
      500: "#AA7714",
    },
    propending: {
      400: "#E6FFFB",
      500: "#112123",
    },

    progressgreen: {
      400: "#6bcf76",
      500: "#6bcf76",
    },
    projectgreen: {
      400: "#3D753E",
      500: "#3D753E",
    },
    projectsharingbg: {
      400: "#F7F8F3",
      500: "#15181F",
    },
    projectlinkbg: {
      50: "#F6FFF6",
      100: "#ECFFEC",
      200: "#D4F9D4",
      300: "#B5F0B5",
      400: "#F6FFF6",
      500: "#1A2E1A",
      600: "#2D5A2D",
      700: "#1F4A1F",
      800: "#143A14",
      900: "#0A2B0A",
      950: "#051C05",
    },
    progressred: {
      400: "#D94F39",
      500: "#D94F39",
    },
    deletered: {
      400: "#dc2626",
      500: "#ef4444",
    },
    progressyellow: {
      400: "#f5a623",
      500: "#f5a623",
    },
    progressgray: {
      400: "#f6f7fd",
      500: "#f6f7fd",
    },
    progresscircleborder: {
      400: "#b9b9be",
      500: "#b9b9be",
    },
    progresscircletext: {
      400: "#565659",
      500: "#565659",
    },
    graycolcal: {
      400: "#565656",
      500: "#565656",
    },
    lightsky: {
      400: "#f3faff",
      500: "#f3faff",
    },
    lightsky2: {
      400: "#abd4fa",
      500: "#abd4fa",
    },
    purplecol: {
      400: "#A557A5",
      500: "#A557A5",
    },
    darkpurplecol: {
      400: "#9747ff",
      500: "#9747ff",
    },
    lightgreen: {
      400: "#78f8b1",
      500: "#78f8b1",
    },
    softgreen: {
      400: "#F7FFF7",
      500: "#1C401C",
    },
    darkgreen: {
      400: "#1b7105",
      500: "#1b7105",
    },

    lightyellow: {
      400: "#FFF8BC",
      500: "#FFF8BC",
    },
    transitstatusbackground: {
      400: "#FFFBEB",
      500: "#FFFBEB",
    },
    transitstatusborder: {
      400: "#FEE685",
      500: "#FEE685",
    },
    darkbrown: {
      400: "#675D03",
      500: "#675D03",
    },
    blackcol: {
      400: "#000",
      500: "#000",
    },
    taskcommit: {
      400: "#eeeeee",
      500: "#eeeeee",
    },
    tasktagbox: {
      400: "#b4bfc9",
      500: "#b4bfc9",
    },
    taskcalendermonth: {
      400: "#313855",
      500: "#313855",
    },
    taskurgentcol: {
      400: "#EF4444",
      500: "#EF4444",
    },
    revisionrequestedbg: {
      400: "#ff4d4d",
      500: "#ff4d4d",
    },
    tablerowtd: {
      400: "#060606",
      500: "#c9cad1",
    },
    taskurgentcolbg: {
      400: "#ffd0d0",
      500: "#9b6060",
    },
    scrolbarcolr: {
      400: "#bdbdbd",
      500: "#bdbdbd",
    },
    dashboardbox1: {
      400: "#EDEEFC",
      500: "#0A1E46",
    },
    dashboardbox2: {
      400: "#E6F1FD",
      500: "#335070",
    },
    tableanchor: {
      400: "#0a2dc0",
      500: "#7e98ff",
    },
    prioritybar: {
      400: "#ECF3FA",
      500: "#ECF3FA",
    },
    taskgradient: {
      400: "linear-gradient(90deg, #F3BEBE66 0%, #FCF6F699 100%)",
      500: "linear-gradient(90deg, rgb(124 15 15 / 92%) 0%, rgb(100 54 54 / 49%) 100%);",
    },
    bggrayourdue: {
      400: "#EFF0F1",
      500: "#151827",
    },
    createnewbg: {
      400: "#F9F5FF",
      500: "#2D1B69",
    },
    createnewtext: {
      400: "#6941C6",
      500: "#B692F6",
    },
    grouplistbg: {
      400: "#F6FBFF",
      500: "#0D1B2A",
    },
    border_bottom: {
      400: "#B6B6B6",
      500: "#2E3441",
    },
    border_bottom_secondary: {
      400: "#F0F0F0",
      500: "#636A7AA6",
    },
    successbtn: {
      400: "#3D753E",
      500: "#fff",
    },
    tabbutton: {
      400: "#fff",
      500: "#333333",
    },
    softblue: {
      400: "#F6FBFF",
      500: "#0A1E46",
    },
    chatbubbleoutgoing: {
      400: "#FFFFFF1A",
      500: "#FFFFFF1A",
    },
    chatStatus: {
      400: "#FFE4E6",
      500: "#FFE4E6",
    },
    chatStatusText: {
      400: "#A50036",
      500: "#A50036",
    },
    chatStatusborder: {
      400: "#FFCCD3",
      500: "#FFCCD3",
    },
    pdficon: {
      400: "#000",
      500: "#fff",
    },
    chatbubbleoutgoingborder: {
      400: "#FFFFFF33",
      500: "#FFFFFF33",
    },
    pendingaccessbg: {
      400: "#FFF7ee",
      500: "#f5a623",
    },
    pendingaccesstext: {
      400: "#FFBE6D",
      500: "#FFF",
    },
  },
  semantic: {
    primary: {
      50: "{nadelblue.50}",
      100: "{nadelblue.100}",
      200: "{nadelblue.200}",
      300: "{nadelblue.300}",
      400: "{nadelblue.400}",
      500: "{nadelblue.500}",
      600: "{nadelblue.600}",
      700: "{nadelblue.700}",
      800: "{nadelblue.800}",
      900: "{nadelblue.900}",
      950: "{nadelblue.950}",
    },
    colorScheme: nadelThemeExtension(
      header_tags_extension,
      header_extension,
      sidebar_extension,
      table_extension,
      text_extension,

      {
        light: {
          primary: {
            color: "{primary.500}",
            contrastColor: "#ffffff",
            hoverColor: "{primary.600}",
            activeColor: "{primary.700}",
          },
          nv: {
            color: "{primary.200}",
          },
          bodybg: {
            color: "#f6f7fd",
          },

          activelink: {
            color: "{activelink.400}",
          },
          scrollbarhover: {
            color: "{scrollbarhover.400}",
          },
          dashboardheader: {
            color: "{dashboardheader.400}",
          },
          dashboardheadertext: {
            color: "{dashboardheadertext.400}",
          },
          tableheaderborder: {
            color: "{tableheaderborder.400}",
          },
          sortrow: {
            color: "{sortrow.400}",
          },
          tableinput: {
            color: "{tableinput.400}",
          },
          tableinputbg: {
            color: "{tableinputbg.400}",
          },
          bgdarkblue: {
            color: "{bgdarkblue.400}",
          },
          bggraybg: {
            color: "{bggraybg.400}",
          },
          alertlightbg: {
            color: "{alertlightbg.400}",
          },
          h2color: {
            color: "{h2color.400}",
          },
          h3color: {
            color: "{h3color.400}",
          },
          textblack: {
            color: "{textblack.400}",
          },
          textgray: {
            color: "{textgray.400}",
          },
          sidebartext: {
            color: "{sidebartext.400}",
          },
          redcoltext: {
            color: "{redcoltext.400}",
          },
          striplisting: {
            color: "{striplisting.400}",
          },
          errorcol: {
            color: "{errorcol.400}",
          },
          focuscol: {
            color: "{focuscol.400}",
          },
          clientbdgecol: {
            color: "{clientbdgecol.400}",
          },
          clientbdgecoltext: {
            color: "{clientbdgecoltext.400}",
          },

          progressgreen: {
            color: "{progressgreen.400}",
          },

          progressred: {
            color: "{progressred.400}",
          },

          progressyellow: {
            color: "{progressyellow.400}",
          },
          progressgray: {
            color: "{progressgray.400}",
          },
          progresscircleborder: {
            color: "{progresscircleborder.400}",
          },
          progresscircletext: {
            color: "{progresscircletext.400}",
          },
          graycolcal: {
            color: "{graycolcal.400}",
          },
          lightsky: {
            color: "{lightsky.400}",
          },
          purplecol: {
            color: "{purplecol.400}",
          },
          lightgreen: {
            color: "{lightgreen.400}",
          },
          darkgreen: {
            color: "{darkgreen.400}",
          },
          softgreen: {
            color: "{softgreen.400}",
          },
          projectgreen: {
            color: "{projectgreen.400}",
          },
          projectsharingbg: {
            color: "{projectsharingbg.400}",
          },
          projectlinkbg: {
            color: "{projectlinkbg.400}",
          },
          deletered: {
            color: "{deletered.400}",
          },
          lightyellow: {
            color: "{lightyellow.400}",
          },
          transitstatusbackground: {
            color: "{transitstatusbackground.400}",
          },
          transitstatusborder: {
            color: "{transitstatusborder.400}",
          },
          darkbrown: {
            color: "{darkbrown.400}",
          },
          lightsky2: {
            color: "{lightsky2.400}",
          },
          blackcol: {
            color: "{blackcol.400}",
          },
          taskcommit: {
            color: "{taskcommit.400}",
          },
          darkred: {
            color: "{darkred.400}",
          },
          taskcalendermonth: {
            color: "{taskcalendermonth.400}",
          },
          taskurgentcol: {
            color: "{taskurgentcol.400}",
          },
          revisionrequestedbg: {
            color: "{revisionrequestedbg.400}",
          },
          tasktagbox: {
            color: "{tasktagbox.400}",
          },
          taskurgentcolbg: {
            color: "{taskurgentcolbg.400}",
          },
          tablerowtd: {
            color: "{tablerowtd.400}",
          },
          scrolbarcolr: {
            color: "{scrolbarcolr.400}",
          },
          dashboardbox1: {
            color: "{dashboardbox1.400}",
          },
          dashboardbox2: {
            color: "{dashboardbox2.400}",
          },
          tableanchor: {
            color: "{tableanchor.400}",
          },
          prioritybar: {
            color: "{prioritybar.400}",
          },
          taskgradient: {
            color: "{taskgradient.400}",
          },
          bggrayourdue: {
            color: "{bggrayourdue.400}",
          },
          createnewbg: {
            color: "{createnewbg.400}",
          },
          createnewtext: {
            color: "{createnewtext.400}",
          },
          grouplistbg: {
            color: "{grouplistbg.400}",
          },
          border_bottom: {
            color: "{border_bottom.400}",
          },
          border_bottom_secondary: {
            color: "{border_bottom_secondary.400}",
          },
          successbtn: {
            color: "{successbtn.400}",
          },
          tabbutton: {
            color: "{tabbutton.400}",
          },
          softblue: {
            color: "{softblue.400}",
          },
          chatbubbleoutgoing: {
            color: "{chatbubbleoutgoing.400}",
          },
          chatStatus: {
            color: "{chatStatus.400}",
          },
          chatStatusText: {
            color: "{chatStatusText.400}",
          },
          chatStatusborder: {
            color: "{chatStatusborder.400}",
          },
          pdficon: {
            color: "{pdficon.400}",
          },
          chatbubbleoutgoingborder: {
            color: "{chatbubbleoutgoingborder.400}",
          },
          pendingaccessbg: {
            color: "{pendingaccessbg.400}",
          },
          pendingaccesstext: {
            color: "{pendingaccesstext.400}",
          },
        },
        dark: {
          primary: {
            color: "{primary.500}",
            contrastColor: "#ffffff",
            hoverColor: "{primary.600}",
            activeColor: "{primary.700}",
          },
          nv: {
            color: "{primary.100}",
          },
          bodybg: {
            color: "#111a2c",
          },
          activelink: {
            color: "{activelink.500}",
          },
          scrollbarhover: {
            color: "{scrollbarhover.500}",
          },
          dashboardheader: {
            color: "{dashboardheader.500}",
          },
          dashboardheadertext: {
            color: "{dashboardheadertext.500}",
          },
          tableheaderborder: {
            color: "{tableheaderborder.500}",
          },
          sortrow: {
            color: "{sortrow.500}",
          },
          tableinput: {
            color: "{tableinput.500}",
          },
          tableinputbg: {
            color: "{tableinputbg.500}",
          },
          bgdarkblue: {
            color: "{bgdarkblue.500}",
          },
          bggraybg: {
            color: "{bggraybg.500}",
          },
          alertlightbg: {
            color: "{alertlightbg.500}",
          },
          h2color: {
            color: "{h2color.500}",
          },
          h3color: {
            color: "{h3color.500}",
          },
          textblack: {
            color: "{textblack.500}",
          },
          textgray: {
            color: "{textgray.500}",
          },
          sidebartext: {
            color: "{sidebartext.500}",
          },
          redcoltext: {
            color: "{redcoltext.500}",
          },
          striplisting: {
            color: "{striplisting.500}",
          },
          errorcol: {
            color: "{errorcol.500}",
          },
          focuscol: {
            color: "{focuscol.500}",
          },
          clientbdgecol: {
            color: "{clientbdgecol.500}",
          },
          clientbdgecoltext: {
            color: "{clientbdgecoltext.500}",
          },

          progressgreen: {
            color: "{progressgreen.500}",
          },

          progressred: {
            color: "{progressred.500}",
          },

          progressyellow: {
            color: "{progressyellow.500}",
          },
          progressgray: {
            color: "{progressgray.500}",
          },
          progresscircleborder: {
            color: "{progresscircleborder.500}",
          },
          progresscircletext: {
            color: "{progresscircletext.500}",
          },
          graycolcal: {
            color: "{graycolcal.500}",
          },
          lightsky: {
            color: "{lightsky.500}",
          },
          purplecol: {
            color: "{purplecol.500}",
          },
          lightgreen: {
            color: "{lightgreen.500}",
          },
          darkgreen: {
            color: "{darkgreen.500}",
          },
          softgreen: {
            color: "{softgreen.500}",
          },
          projectgreen: {
            color: "{projectgreen.500}",
          },
          projectsharingbg: {
            color: "{projectsharingbg.500}",
          },
          projectlinkbg: {
            color: "{projectlinkbg.500}",
          },
          deletered: {
            color: "{deletered.500}",
          },
          lightyellow: {
            color: "{lightyellow.500}",
          },
          transitstatusbackground: {
            color: "{transitstatusbackground.500}",
          },
          transitstatusborder: {
            color: "{transitstatusborder.500}",
          },
          darkbrown: {
            color: "{darkbrown.500}",
          },
          lightsky2: {
            color: "{lightsky2.500}",
          },
          blackcol: {
            color: "{blackcol.500}",
          },
          taskcommit: {
            color: "{taskcommit.500}",
          },
          darkred: {
            color: "{darkred.500}",
          },
          taskcalendermonth: {
            color: "{taskcalendermonth.500}",
          },
          taskurgentcol: {
            color: "{taskurgentcol.500}",
          },
          revisionrequestedbg: {
            color: "{revisionrequestedbg.500}",
          },
          tasktagbox: {
            color: "{tasktagbox.500}",
          },
          taskurgentcolbg: {
            color: "{taskurgentcolbg.500}",
          },
          tablerowtd: {
            color: "{tablerowtd.500}",
          },
          scrolbarcolr: {
            color: "{scrolbarcolr.500}",
          },
          dashboardbox1: {
            color: "{dashboardbox1.500}",
          },
          dashboardbox2: {
            color: "{dashboardbox2.500}",
          },
          tableanchor: {
            color: "{tableanchor.500}",
          },
          prioritybar: {
            color: "{prioritybar.500}",
          },
          taskgradient: {
            color: "{taskgradient.500}",
          },
          bggrayourdue: {
            color: "{bggrayourdue.500}",
          },
          createnewbg: {
            color: "{createnewbg.500}",
          },
          createnewtext: {
            color: "{createnewtext.500}",
          },
          grouplistbg: {
            color: "{grouplistbg.500}",
          },
          border_bottom: {
            color: "{border_bottom.500}",
          },
          border_bottom_secondary: {
            color: "{border_bottom_secondary.500}",
          },
          successbtn: {
            color: "{successbtn.500}",
          },
          tabbutton: {
            color: "{tabbutton.500}",
          },
          softblue: {
            color: "{softblue.500}",
          },
          chatbubbleoutgoing: {
            color: "{chatbubbleoutgoing.500}",
          },
          chatStatus: {
            color: "{chatStatus.500}",
          },
          chatStatusText: {
            color: "{chatStatusText.500}",
          },
          chatStatusborder: {
            color: "{chatStatusborder.500}",
          },
          pdficon: {
            color: "{pdficon.500}",
          },
          chatbubbleoutgoingborder: {
            color: "{chatbubbleoutgoingborder.500}",
          },
          pendingaccessbg: {
            color: "{pendingaccessbg.500}",
          },
          pendingaccesstext: {
            color: "{pendingaccesstext.500}",
          },
        },
      }
    ),
  },
});

export { Nadel };
